import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import SemiTruck from "@/components/icons/SemiTruck";
import { toast } from "sonner";

const BASE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrintInvoice() {
  const { id, businessSlug } = useParams();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [settings, setSettings] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasPrinted, setHasPrinted] = useState(false);
  // Per-business line-item type config — so we render the rename
  // ("Part" → custom_2's label, etc.) instead of the raw key on the
  // printed invoice. Buyers should never see "CUSTOM_2".
  const [lineItemTypes, setLineItemTypes] = useState([]);
  const labelForType = (key) => {
    const row = lineItemTypes.find((t) => t.key === key);
    if (row?.label) return row.label;
    if (!key) return "";
    return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
  };
  const isInventoryType = (key) => {
    const row = lineItemTypes.find((t) => t.key === key);
    if (row) return !!row.track_inventory;
    return key === "part";
  };
  
  const API = `${BASE_API}/b/${businessSlug}`;
  const basePath = `/${businessSlug}`;

  // Dismiss all toasts when print page loads (removes "Invoice created successfully" etc.)
  useEffect(() => {
    toast.dismiss();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [invoiceRes, settingsRes, businessRes, lineTypesRes] = await Promise.all([
        axios.get(`${API}/invoices/${id}`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/info`),
        axios.get(`${API}/line-item-types`).catch(() => ({ data: { line_item_types: [] } })),
      ]);
      setInvoice(invoiceRes.data);
      setSettings(settingsRes.data);
      setBusinessInfo(businessRes.data);
      setLineItemTypes(lineTypesRes.data?.line_item_types || []);
    } catch (error) {
      console.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [id, API]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-print disabled - user prefers manual printing
  // useEffect(() => {
  //   const autoPrint = searchParams.get('auto');
  //   if (autoPrint === 'true' && !loading && invoice && !hasPrinted) {
  //     const timer = setTimeout(() => {
  //       window.print();
  //       setHasPrinted(true);
  //     }, 500);
  //     return () => clearTimeout(timer);
  //   }
  // }, [searchParams, loading, invoice, hasPrinted]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        Loading invoice...
      </div>
    );
  }

  if (!invoice) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        Invoice not found
      </div>
    );
  }

  const business = businessInfo || {
    name: businessSlug?.toUpperCase() || "Business",
    address: "",
    phone: "",
    zelle_email: ""
  };
  
  // Get Zelle info from settings (preferred) or business info
  const zelleEmail = settings?.zelle_info?.zelle_email || business.zelle_email || "";
  const zellePhone = settings?.zelle_info?.zelle_phone || "";
  const zelleQrCode = settings?.zelle_info?.zelle_qr_code || "";
  
  // Get short name for thank you message
  const shortName = business.name?.split(' ')[0] || businessSlug?.toUpperCase() || "Us";

  return (
    <>
      {/* Print Styles */}
      <style>
        {`
          @media print {
            .no-print { display: none !important; }
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            img { max-width: 100% !important; }
            #printable-invoice { margin: 0 !important; padding: 0.25in !important; }
          }
        `}
      </style>
      
      {/* Back button - hidden when printing OR when embedded as a preview */}
      {searchParams.get("preview") !== "1" && (
        <div className="no-print" style={{ padding: '16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: '12px', alignItems: 'center' }}>
          <Link to={`${basePath}/invoices/${id}`}>
            <Button variant="outline" size="sm" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1e40af', borderColor: '#93c5fd' }}>
              <ArrowLeft style={{ width: '16px', height: '16px' }} />
              Back to Invoice
            </Button>
          </Link>
          <Button
            onClick={handlePrint}
            size="sm"
            style={{ background: '#2563eb', color: 'white', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Printer style={{ width: '16px', height: '16px' }} />
            Print Invoice
          </Button>
        </div>
      )}

      {/* Printable Invoice - Clean White Receipt */}
      <div id="printable-invoice" style={{
        width: '100%',
        maxWidth: '8.5in',
        minHeight: '10in',
        margin: '0 auto',
        padding: '0.5in',
        background: 'white',
        color: 'black',
        fontFamily: 'Arial, sans-serif',
        fontSize: '12px',
        lineHeight: '1.4',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header with Logo */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          borderBottom: '3px solid #2563eb',
          paddingBottom: '16px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Logo - using base64 from backend for reliable printing */}
            {(businessInfo?.logo_base64 || businessInfo?.logo_url) && (
              <div style={{
                width: '80px',
                height: '80px',
                background: '#fff',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                border: '1px solid #e2e8f0'
              }}>
                <img 
                  src={businessInfo?.logo_base64 || businessInfo?.logo_url} 
                  alt="Business Logo"
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'contain',
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact'
                  }}
                  onError={(e) => { 
                    console.log('Logo failed to load');
                    e.target.parentElement.style.display = 'none'; 
                  }}
                />
              </div>
            )}
            <div>
              <h1 style={{ fontSize: '26px', fontWeight: 'bold', margin: 0, color: '#1e3a8a' }}>
                {business.name}
              </h1>
              {business.address && (
                <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#64748b' }}>
                  {business.address}
                </p>
              )}
              {business.phone && (
                <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#64748b' }}>
                  Phone: {business.phone}
                </p>
              )}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 'bold', margin: 0, color: '#1e3a8a' }}>INVOICE</h2>
            <p style={{ margin: '4px 0', fontSize: '14px', fontWeight: 'bold' }}>{invoice.invoice_number}</p>
            <p style={{ margin: '2px 0', fontSize: '11px', color: '#64748b' }}>
              Date: {new Date(invoice.created_at).toLocaleDateString()}
            </p>
            <p style={{ 
              margin: '8px 0 0 0', 
              fontSize: '12px', 
              fontWeight: 'bold',
              padding: '4px 12px',
              background: invoice.payment_status === 'paid' ? '#dcfce7' : '#fef3c7',
              color: invoice.payment_status === 'paid' ? '#166534' : '#92400e',
              display: 'inline-block',
              borderRadius: '4px'
            }}>
              {invoice.payment_status.toUpperCase()}
            </p>
          </div>
        </div>

        {/* Bill To Section */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: '9px', fontWeight: 'bold', margin: '0 0 6px 0', color: '#64748b', textTransform: 'uppercase' }}>Bill To:</p>
            <p style={{ margin: '0', fontWeight: 'bold', fontSize: '12px' }}>{invoice.customer_name}</p>
            {invoice.company_name && (
              <p style={{ margin: '2px 0', fontSize: '10px', color: '#475569' }}>{invoice.company_name}</p>
            )}
            {invoice.customer_address && <p style={{ margin: '2px 0', fontSize: '10px', color: '#64748b' }}>{invoice.customer_address}</p>}
            {invoice.customer_phone && <p style={{ margin: '2px 0', fontSize: '10px', color: '#64748b' }}>Phone: {invoice.customer_phone}</p>}
            {invoice.customer_email && <p style={{ margin: '2px 0', fontSize: '10px', color: '#64748b' }}>{invoice.customer_email}</p>}
          </div>
          <div style={{ textAlign: 'right' }}>
            {invoice.vehicle_info && (
              <div>
                <p style={{ fontSize: '9px', fontWeight: 'bold', margin: '0 0 4px 0', color: '#64748b', textTransform: 'uppercase' }}>Vehicle:</p>
                <p style={{ margin: '0', fontSize: '10px' }}>{invoice.vehicle_info}</p>
              </div>
            )}
            {invoice.truck_number && (
              <p style={{ margin: '4px 0', fontSize: '10px' }}>Truck #: <strong>{invoice.truck_number}</strong></p>
            )}
          </div>
        </div>

        {/* Line Items Table */}
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          marginBottom: '16px'
        }}>
          <thead>
            <tr style={{ background: '#f1f5f9' }}>
              <th style={{ padding: '8px 6px', textAlign: 'left', fontSize: '9px', fontWeight: 'bold', borderBottom: '2px solid #cbd5e1', textTransform: 'uppercase' }}>Description</th>
              <th style={{ padding: '8px 6px', textAlign: 'center', fontSize: '9px', fontWeight: 'bold', borderBottom: '2px solid #cbd5e1', width: '50px', textTransform: 'uppercase' }}>Type</th>
              <th style={{ padding: '8px 6px', textAlign: 'center', fontSize: '9px', fontWeight: 'bold', borderBottom: '2px solid #cbd5e1', width: '40px', textTransform: 'uppercase' }}>Qty</th>
              <th style={{ padding: '8px 6px', textAlign: 'right', fontSize: '9px', fontWeight: 'bold', borderBottom: '2px solid #cbd5e1', width: '70px', textTransform: 'uppercase' }}>Price</th>
              <th style={{ padding: '8px 6px', textAlign: 'right', fontSize: '9px', fontWeight: 'bold', borderBottom: '2px solid #cbd5e1', width: '80px', textTransform: 'uppercase' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((item, index) => (
              <tr key={index} style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '8px 6px', fontSize: '10px' }}>{item.description}</td>
                <td style={{ padding: '8px 6px', textAlign: 'center', fontSize: '9px' }}>
                  <span style={{ 
                    padding: '1px 4px', 
                    background: isInventoryType(item.type) ? '#dbeafe' : '#f3e8ff',
                    color: isInventoryType(item.type) ? '#1e40af' : '#7c3aed',
                    borderRadius: '3px',
                    fontSize: '8px',
                    fontWeight: '500'
                  }}>
                    {labelForType(item.type).toUpperCase()}
                  </span>
                </td>
                <td style={{ padding: '8px 6px', textAlign: 'center', fontSize: '10px' }}>{item.quantity}</td>
                <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: '10px' }}>{formatCurrency(item.unit_price)}</td>
                <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: '10px', fontWeight: '500' }}>{formatCurrency(item.quantity * item.unit_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: '220px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: '10px' }}>
              <span>Subtotal:</span>
              <span>{formatCurrency(invoice.subtotal)}</span>
            </div>
            {!invoice.tax_exempt && invoice.tax_rate > 0 && invoice.tax_amount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: '10px' }}>
                <span>Tax ({(invoice.tax_rate * 100).toFixed(2)}%):</span>
                <span>{formatCurrency(invoice.tax_amount)}</span>
              </div>
            )}
            {(invoice.tax_exempt || invoice.tax_rate === 0 || invoice.tax_amount === 0) && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: '10px', color: '#92400e' }}>
                <span>Tax:</span>
                <span>TAX EXEMPT</span>
              </div>
            )}
            {invoice.card_fee_enabled && invoice.card_fee > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: '10px', color: '#2563eb' }}>
                <span>Card Fee (3%):</span>
                <span>{formatCurrency(invoice.card_fee)}</span>
              </div>
            )}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '10px 0', 
              borderTop: '3px solid #1e3a8a',
              marginTop: '6px',
              fontWeight: 'bold',
              fontSize: '14px',
              color: '#1e3a8a'
            }}>
              <span>TOTAL:</span>
              <span>{formatCurrency(invoice.total)}</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {invoice.notes && (
          <div style={{ marginTop: '16px', padding: '10px', background: '#f8fafc', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
            <p style={{ margin: '0 0 4px 0', fontWeight: 'bold', fontSize: '9px', color: '#64748b', textTransform: 'uppercase' }}>Notes:</p>
            <p style={{ margin: 0, fontSize: '10px' }}>{invoice.notes}</p>
          </div>
        )}

        {/* Spacer to push footer to bottom */}
        <div style={{ flexGrow: 1, minHeight: '40px' }}></div>

        {/* Footer Section - Always at bottom */}
        <div style={{ 
          marginTop: 'auto',
          paddingTop: '20px',
          borderTop: '1px solid #e2e8f0'
        }}>
          {/* Payment Info with QR Code */}
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '12px'
          }}>
            <div style={{ flex: 1 }}>
              <p style={{ margin: '0 0 4px 0', fontWeight: 'bold', fontSize: '9px', color: '#64748b', textTransform: 'uppercase' }}>Payment Options:</p>
              {zelleEmail && (
                <p style={{ margin: '0', fontSize: '10px' }}>
                  <strong>Zelle:</strong> {zelleEmail}
                </p>
              )}
              {zellePhone && (
                <p style={{ margin: '2px 0 0 0', fontSize: '10px' }}>
                  <strong>Zelle:</strong> {zellePhone}
                </p>
              )}
              <p style={{ margin: '4px 0 0 0', fontSize: '9px', color: '#64748b' }}>
                Also accept: Card, Cash App, Cash, Check
              </p>
            </div>
          </div>

          {/* Thank You Message */}
          <div style={{ 
            textAlign: 'center', 
            paddingTop: '10px',
            borderTop: '1px dashed #e2e8f0'
          }}>
            <p style={{ margin: '0', fontSize: '11px', fontWeight: 'bold', color: '#1e3a8a' }}>
              Thank you for choosing {shortName}!
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: '9px', color: '#64748b' }}>
              We appreciate your business. Drive safe!
            </p>
          </div>
        </div>
      </div>

      {/* Print-specific styles */}
      <style>{`
        @media print {
          @page {
            size: letter;
            margin: 0.3in;
          }
          body {
            margin: 0;
            padding: 0;
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print {
            display: none !important;
          }
          #printable-invoice {
            padding: 0 !important;
            max-width: 100% !important;
          }
          /* Ensure images print properly */
          img {
            max-width: 100% !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            display: block !important;
          }
          /* Hide any Emergent branding */
          [class*="emergent"],
          [data-testid*="emergent"],
          img[src*="emergent"][src*="preview"] {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
}
